import { UNITS_OF_MEASURE } from './constants';

export interface UnitOfMeasureOption {
    value: string;
    label: string;
}

/**
 * Legacy labels/values → canonical UNITS_OF_MEASURE.value.
 * Covers short codes (hrs, mi), singulars (hour, mile), and old capitalized storage.
 */
const UNIT_OF_MEASURE_ALIASES: Record<string, string> = {
    hr: 'hours',
    hrs: 'hours',
    hour: 'hours',
    mi: 'miles',
    mile: 'miles',
    gal: 'gallons',
    gallon: 'gallons',
    km: 'kilometers',
    kilometer: 'kilometers',
    kilometres: 'kilometers',
    kilometre: 'kilometers',
    cycle: 'cycles',
    liter: 'liters',
    litre: 'liters',
    litres: 'liters',
    day: 'days',
    count: 'counts',
    ea: 'each',
};

const CANONICAL_PRESET_VALUES = new Set<string>(UNITS_OF_MEASURE.map((unit) => unit.value));

const titleCaseSegment = (segment: string): string => {
    if (!segment) return segment;
    return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
};

/** Format a stored custom UM for display (underscores → spaces, capitalized). */
export const formatCustomUnitOfMeasureDisplay = (value: string): string =>
    value
        .split('_')
        .map((part) => part.split('-').map(titleCaseSegment).join('-'))
        .join(' ');

const compactUnitOfMeasureKey = (raw: string): string => raw.trim().toLowerCase().replace(/\s+/g, '_');

/** True when the stored string is already a canonical preset value (e.g. "hours"). */
export const isCanonicalUnitOfMeasureValue = (value: string): boolean =>
    CANONICAL_PRESET_VALUES.has(value);

/** Map any known label/alias to a canonical preset value, or null if not a preset. */
export const resolveCanonicalUnitOfMeasureValue = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;

    const byLabel = UNITS_OF_MEASURE.find((unit) => unit.label.toLowerCase() === trimmed.toLowerCase());
    if (byLabel) return byLabel.value;

    const compact = compactUnitOfMeasureKey(trimmed);
    const byValue = UNITS_OF_MEASURE.find((unit) => unit.value === compact);
    if (byValue) return byValue.value;

    const alias = UNIT_OF_MEASURE_ALIASES[compact];
    if (alias) return alias;

    return null;
};

/** Stored value: lowercase canonical preset, or normalized custom (spaces → underscores). */
export const normalizeUnitOfMeasure = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return '';

    const canonical = resolveCanonicalUnitOfMeasureValue(trimmed);
    if (canonical) return canonical;

    return trimmed.toLowerCase().replace(/\s+/g, '_');
};

/** Resolve user input to a canonical stored UM value. */
export const resolveUnitOfMeasureInput = normalizeUnitOfMeasure;

export type UnitOfMeasureCatalogItem = {
    value: string;
    name?: string;
    label?: string;
};

const displayUnitOfMeasureFallback = (trimmed: string): string => {
    const canonical = resolveCanonicalUnitOfMeasureValue(trimmed);
    const preset = canonical ? UNITS_OF_MEASURE.find((unit) => unit.value === canonical) : undefined;

    if (preset) {
        if (isCanonicalUnitOfMeasureValue(trimmed)) {
            return preset.label;
        }
        return trimmed;
    }

    return formatCustomUnitOfMeasureDisplay(trimmed);
};

/**
 * Display label for any stored UM.
 * When a company catalog is provided, renamed catalog names win over presets.
 */
export const displayUnitOfMeasure = (
    value: string | null | undefined,
    catalog?: UnitOfMeasureCatalogItem[],
): string => {
    if (!value) return '';

    const trimmed = value.trim();
    if (catalog && catalog.length > 0) {
        const exact = catalog.find((item) => item.value === trimmed);
        if (exact) {
            const catalogName = (exact.name || exact.label || '').trim();
            if (catalogName && catalogName !== exact.value) return catalogName;
            return displayUnitOfMeasureFallback(trimmed);
        }

        const canonical = resolveCanonicalUnitOfMeasureValue(trimmed) || normalizeUnitOfMeasure(trimmed);
        const byValue = catalog.find((item) => item.value === canonical);
        if (byValue) {
            const catalogName = (byValue.name || byValue.label || '').trim();
            if (catalogName && catalogName !== byValue.value) return catalogName;
            return displayUnitOfMeasureFallback(trimmed);
        }
    }

    return displayUnitOfMeasureFallback(trimmed);
};

/**
 * Catalog `name` for a typed or stored UM.
 * Keeps “Quart”; title-cases a stored slug like “quart”.
 */
export const catalogDisplayNameForUnitOfMeasure = (rawLabel: string): string => {
    const name = rawLabel.trim();
    if (!name) return '';
    const value = normalizeUnitOfMeasure(name);
    if (name !== value) return name;
    return displayUnitOfMeasure(value) || name;
};

/** True when `stored` is already a catalog row (slug or display name). */
export const isExactCatalogUnitOfMeasure = (
    stored: string | null | undefined,
    catalog?: UnitOfMeasureCatalogItem[],
): boolean => {
    if (!stored?.trim() || !catalog?.length) return false;
    const trimmed = stored.trim();
    return catalog.some((item) => {
        const name = (item.name || item.label || '').trim();
        return (
            item.value === trimmed ||
            item.value.toLowerCase() === trimmed.toLowerCase() ||
            (name.length > 0 && name.toLowerCase() === trimmed.toLowerCase())
        );
    });
};

/** Catalog row that a legacy stored UM (e.g. "Gal") should be updated to, if any. */
export const findSuggestedCatalogUnitOfMeasure = (
    stored: string | null | undefined,
    catalog?: UnitOfMeasureCatalogItem[],
): UnitOfMeasureCatalogItem | undefined => {
    if (!stored?.trim() || !catalog?.length) return undefined;
    if (isExactCatalogUnitOfMeasure(stored, catalog)) return undefined;
    const trimmed = stored.trim();
    const canonical = resolveCanonicalUnitOfMeasureValue(trimmed) || normalizeUnitOfMeasure(trimmed);
    return catalog.find((item) => item.value === canonical);
};

/** Build SearchSuggest options: catalog (or seed presets) first, then extras not already listed. */
export const buildUnitOfMeasureOptions = (
    extraValues: string[] = [],
    catalog?: UnitOfMeasureCatalogItem[],
): UnitOfMeasureOption[] => {
    const presetOptions: UnitOfMeasureOption[] =
        catalog && catalog.length > 0
            ? catalog.map((item) => ({
                  value: item.value,
                  label: item.name || item.label || displayUnitOfMeasureFallback(item.value),
              }))
            : UNITS_OF_MEASURE.map((unit) => ({
                  value: unit.value,
                  label: unit.label,
              }));

    const listedValues = new Set(presetOptions.map((option) => option.value));
    const listedLabels = new Set(presetOptions.map((option) => option.label.toLowerCase()));
    const customValues = new Set<string>();

    extraValues.forEach((raw) => {
        const trimmed = raw.trim();
        if (!trimmed) return;
        if (listedValues.has(trimmed) || listedLabels.has(trimmed.toLowerCase())) return;
        customValues.add(trimmed);
    });

    const customOptions = [...customValues]
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .map((value) => ({
            value,
            label: isExactCatalogUnitOfMeasure(value, catalog)
                ? displayUnitOfMeasure(value, catalog)
                : value,
        }));

    return [...presetOptions, ...customOptions].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
};
